import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { clsx } from 'clsx';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';
import { Download } from 'lucide-react';
import { COMPANY_ROLE } from '@/models/references/references';
import { useCurrentCompany } from '@/stores/useCompanyStore';
import { useExportStore } from '@/stores/useExportStore';
import { useReferences } from '@/stores/useReferenceOptions';
import { useFilterNames } from './useFilterNames';
import { ExportAction, ExportFiltersLabel, ExportStatusBadge } from './ExportParts';

export const EXPORTS_PATH = '/exports';

/**
 * Icône Téléchargements de l'en-tête, à côté de la cloche : les derniers exports de la société sélectionnée (Dirigeant ;
 * « Aucun export » pour les autres, sans appel à l'API), leur statut, Télécharger ou Relancer, et le lien vers tous les
 * exports. Pastille : animée tant qu'un export est en cours, verte quand un export vient de se terminer (jusqu'à
 * l'ouverture du panneau).
 */
export function DownloadsMenu() {
  const [open, setOpen] = useState(false);
  // Ouvert au moins une fois : les noms des filtres ne servent qu'au panneau. Le menu est monté deux fois (en-têtes
  // mobile et desktop, l'un masqué par CSS) : seul celui qu'on ouvre les charge
  const [opened, setOpened] = useState(false);
  const company = useCurrentCompany();
  const companyId = company?.companyRole === COMPANY_ROLE.DIRECTOR ? company.id : null;
  const { exports, loaded, unseen, follow, markSeen } = useExportStore();
  const names = useFilterNames(opened ? exports : []);
  const running = exports.some((model) => model.isRunning);
  useEffect(() => {
    void follow(companyId);
  }, [companyId, follow]);

  const toggle = () => {
    if (!open) {
      markSeen();
      setOpened(true);
    }
    setOpen(!open);
  };

  return (
    <div className="relative">
      {/* Listes des exports : chargées pour le Dirigeant seulement (les autres n'en ont pas) */}
      {companyId !== null && <ExportReferences />}
      <button
        onClick={toggle}
        aria-label={
          running
            ? 'Téléchargements (export en cours)'
            : unseen
              ? `Téléchargements (${unseen} prêt${unseen > 1 ? 's' : ''})`
              : 'Téléchargements'
        }
        aria-expanded={open}
        className={clsx(
          'relative p-2 rounded-full transition-colors',
          open ? 'bg-blue-50 text-blue-600' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100',
        )}
      >
        <Download className="w-5 h-5" />
        {running ? (
          <span
            className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"
            data-testid="downloads-running"
          />
        ) : unseen > 0 ? (
          <span
            className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-green-500"
            data-testid="downloads-unseen"
          />
        ) : null}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            role="dialog"
            aria-label="Téléchargements"
            className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-card rounded-xl shadow-lg border border-slate-200 z-20 overflow-hidden"
          >
            <div className="p-3 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-semibold text-sm text-slate-900">Téléchargements</h3>
            </div>
            {!companyId || (loaded && !exports.length) ? (
              <p className="p-4 text-sm text-slate-500 text-center">Aucun export</p>
            ) : !loaded || !names ? (
              <p className="p-4 text-sm text-slate-500 text-center">Chargement…</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {exports.map((model) => (
                  <li key={model.id} className="p-3 flex items-center gap-3" data-testid="download-item">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900">{model.exportTypeLabel}</p>
                      <p className="text-xs text-slate-500 truncate">
                        <ExportFiltersLabel model={model} names={names} />
                      </p>
                      <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                        {model.createdAt?.format(DATE_TIME_FORMAT)}
                        <ExportStatusBadge model={model} />
                      </div>
                    </div>
                    <ExportAction model={model} />
                  </li>
                ))}
              </ul>
            )}
            {companyId && (
              <Link
                to={EXPORTS_PATH}
                onClick={() => setOpen(false)}
                className="block p-3 text-center text-sm font-medium text-blue-700 hover:bg-slate-50 border-t border-slate-100"
              >
                Voir tous les exports
              </Link>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Charge les listes de référence des exports (types, statuts) et des feuilles (type, pour les filtres en clair) */
function ExportReferences() {
  useReferences('exportTypes', 'exportStatuses', 'timeSheetTypes');
  return null;
}
